import {
    ExercisesSeenContext,
    UnseenExerciseState,
} from "@/Contexts/ExercisesSeenContext";
import classNames from "@/utils/classNames";
import { formatEstimatedMinutes } from "@/utils/formatEstimatedMinutes";
import { formatDistanceToNowStrict } from "date-fns";
import { useContext } from "react";
import { FaBookmark } from "react-icons/fa6";
import { GoArrowUpRight } from "react-icons/go";
import { LuClock } from "react-icons/lu";
import PrimaryButton from "../UI/Forms/PrimaryButton";
import SecondaryButton from "../UI/Forms/SecondaryButton";
import Tooltip from "../UI/Tooltip";
import exerciseIconUrl from "./exerciseIconUrl";
import { ExerciseButton } from "./ExerciseLibrary";
import { Exercise, ViewMode } from "./exerciseSchema";

export type RecommendedExercise = Exercise & {
    recommended: boolean;
    recommendationExplanation?: string | null;
};

export function ExerciseCard({
    exercise,
    unseenState,
    lastSentDate,
    buttons = [],
}: {
    exercise: RecommendedExercise;
    unseenState: UnseenExerciseState | undefined;
    lastSentDate: string | undefined;
    buttons?: (ExerciseButton | ((exercise: Exercise) => ExerciseButton))[];
}) {
    const { setSeen } = useContext(ExercisesSeenContext);

    const estimatedMinutes = formatEstimatedMinutes(exercise.estimated_minutes);

    const resolvedButtons = buttons.map((btnResolver) =>
        typeof btnResolver === "function" ? btnResolver(exercise) : btnResolver,
    );

    const PreviewButton = resolvedButtons.find((button) => button.makePrimary)
        ? SecondaryButton
        : PrimaryButton;

    return (
        <div
            key={`${exercise.id}-${exercise.recommended ? "recommended" : ""}`}
            data-testid={`data-exercise-${exercise.id}`}
            className="group relative flex flex-col rounded-lg border border-brand-border p-3 bg-brand-white overflow-hidden"
        >
            {unseenState && (
                <div
                    data-testid="exercise-unseen-banner"
                    className={classNames(
                        "z-10 absolute rotate-45 top-[28px] -right-[80px] w-64 text-brand-white px-2 py-1 text-sm font-bold text-center shadow-sm",
                        unseenState === "new"
                            ? "bg-brand-red"
                            : "bg-brand-green",
                    )}
                >
                    {unseenState.toUpperCase()}
                </div>
            )}
            {exercise.recommended ? (
                <Tooltip
                    title={
                        <div className="bg-brand-white text-brand-black p-3 max-w-xs">
                            <div className="text-sm font-bold mb-1">
                                Why we recommend this
                            </div>
                            <div className="text-sm">
                                {exercise.recommendationExplanation}
                            </div>
                        </div>
                    }
                    position="top"
                    disabled={!exercise.recommendationExplanation}
                    wrapperContainerClassName="absolute bottom-0 right-0"
                    popupContainerClassName="bg-brand-white text-brand-black drop-shadow-top shadow-lg border border-brand-border rounded-xl p-0"
                >
                    <div
                        data-testid="recommended-badge"
                        className="rounded-tl-lg bg-brand-gray text-brand-white px-2 py-1 text-sm cursor-default"
                    >
                        Recommended
                    </div>
                </Tooltip>
            ) : lastSentDate ? (
                <div className="absolute rounded-tl-lg bottom-0 right-0 bg-brand-gray text-brand-white px-2 py-1 text-sm">
                    {" "}
                    sent{" "}
                    {formatDistanceToNowStrict(new Date(lastSentDate), {
                        addSuffix: true,
                    })}
                </div>
            ) : null}
            <div className="flex gap-3 mb-2">
                <img
                    src={exerciseIconUrl(exercise.icon)}
                    alt={exercise.name}
                    data-testid="exercise-icon"
                    className="h-16 aspect-square object-cover rounded-lg"
                />
                <div className="flex flex-col">
                    <div
                        data-testid="exercise-name"
                        className="font-bold text-lg text-brand-black mb-1 leading-tight"
                    >
                        {exercise.is_bookmarked && (
                            <span className="inline-block mr-1">
                                <FaBookmark className="-mb-[2px]" size={17} />
                            </span>
                        )}
                        {exercise.name}
                    </div>
                    {((exercise.categories && exercise.categories.length > 0) ||
                        estimatedMinutes) && (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5 text-xs font-medium uppercase text-brand-gray">
                            {exercise.categories?.map((category) => (
                                <span
                                    key={category.key}
                                    className="bg-brand-blueDecal/50 rounded-full px-2 py-0.5"
                                >
                                    {category.pretty_name}
                                </span>
                            ))}
                            {estimatedMinutes && (
                                <span
                                    data-testid="exercise-estimated-time"
                                    className="flex items-center gap-1 bg-brand-blueDecal/50 rounded-full px-2 py-0.5"
                                >
                                    <LuClock />
                                    {estimatedMinutes}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex-1 flex flex-col mb-2">
                <div
                    data-testid="exercise-description"
                    className="text-sm text-brand-gray"
                >
                    {exercise.description}
                </div>
            </div>
            {exercise.sample_questions && (
                <Tooltip
                    title={
                        <div className="bg-brand-white text-brand-black w-full h-full p-3 min-w-64">
                            <div className="text-sm font-bold text-brand-black mb-2">
                                Sample questions
                            </div>
                            <ul className="list-disc list-inside text-brand-black">
                                {exercise.sample_questions.map((question) => (
                                    <li key={question} className="mb-1">
                                        {question}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    }
                    wrapperContainerClassName="w-fit"
                    popupContainerClassName="bg-brand-white text-brand-black drop-shadow-top shadow-lg border border-brand-border rounded-xl p-2"
                >
                    <div className="text-sm text-brand-red font-medium mb-2">
                        View sample questions
                    </div>
                </Tooltip>
            )}
            <div
                data-testid="exercise-preview-options"
                className="flex gap-2 mt-auto"
            >
                {resolvedButtons.map((button) => (
                    <Tooltip title={button.tooltipTitle} key={button.id}>
                        <button
                            data-testid={`${button.id}-button`}
                            className={classNames(
                                "flex h-10 w-10 items-center justify-center rounded-lg transition-all",
                                button.makePrimary
                                    ? "bg-brand-red text-brand-white hover:bg-brand-red/80"
                                    : "bg-brand-white border border-brand-red text-brand-red hover:bg-brand-red/20",
                            )}
                            style={button.styles}
                            aria-label={`${button.id} ${exercise.name}`}
                            onClick={() => button.onClick(exercise)}
                        >
                            {button.buttonIcon}
                        </button>
                    </Tooltip>
                ))}
                <PreviewButton
                    onClick={() => {
                        window.open(
                            route("exercises.show", {
                                exercise: exercise.id,
                                viewMode: ViewMode.Preview,
                            }),
                            "_blank",
                        );
                        setSeen(exercise.id);
                    }}
                >
                    Preview <GoArrowUpRight className="ml-1 -mr-3" />
                </PreviewButton>
            </div>
        </div>
    );
}
